import { Box, Button, Chip, IconButton, MenuItem, Stack, TextField, Tooltip } from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { DataTable } from '../../shared/components/DataTable/DataTable';
import { PageHeader } from '../../shared/components/PageHeader';
import { EntitySelect } from '../../shared/components/EntitySelect';
import { ConfirmDialog } from '../../shared/components/Dialogs/ConfirmDialog';
import { JobDetailDialog } from './JobDetailDialog';
import { resources } from '../../core/services/resources';
import { extractErrorMessage } from '../../core/services/api';
import type { ListQuery } from '../../core/types/api';
import type { Job, JobStatus, JobType, Project } from '../../core/types/data-models';

const CANCELLABLE_STATUSES: JobStatus[] = ['PENDING', 'PROCESSING'];

const JOB_TYPES: JobType[] = [
  'GENERATE_DOCUMENT',
  'GENERATE_DOCUMENT_QUESTIONS',
  'REGENERATE_DOCUMENT_SECTION',
  'GENERATE_DOCUMENT_SECTIONS',
  'GENERATE_DOCUMENT_METADATA',
  'SCORE_DOCUMENT_SECTIONS',
  'EXTRACT_DOCUMENT_ENTITIES',
  'GENERATE_DOCUMENT_VISUALIZATION',
  'GENERATE_DOCUMENT_PRESENTATION',
  'GENERATE_VALIDATION_SUMMARY',
  'ANALYZE_PROJECT_SOURCES',
  'EXAMPLE',
];

const JOB_STATUSES: JobStatus[] = ['PENDING', 'PROCESSING', 'COMPLETED', 'FAILED', 'CANCELLED'];

const STATUS_COLORS: Record<JobStatus, 'default' | 'info' | 'success' | 'error'> = {
  PENDING: 'default',
  PROCESSING: 'info',
  COMPLETED: 'success',
  FAILED: 'error',
  CANCELLED: 'default',
};

/** Jobs have no dedicated startedAt/completedAt field, so a completed job's
 * running time is approximated as updatedAt - createdAt (the last write is
 * the one that marked it COMPLETED). */
function formatDuration(job: Job): string {
  const ms = new Date(job.updatedAt).getTime() - new Date(job.createdAt).getTime();
  if (ms < 0) return '—';
  const totalSeconds = Math.round(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

/**
 * Audit log of background `Job` records. Server-side filterable by project,
 * job type, and status; a row click opens the full record in a view-only
 * dialog. The only mutation surfaced here is cancelling a still-active
 * (PENDING/PROCESSING) job — there is no create/edit/delete.
 */
export function JobMonitoringPage() {
  const queryClient = useQueryClient();
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
  const [projectId, setProjectId] = useState<number | null>(null);
  const [jobType, setJobType] = useState<JobType | 'all'>('all');
  const [status, setStatus] = useState<JobStatus | 'all'>('all');
  const [selected, setSelected] = useState<Job | null>(null);
  const [cancelTarget, setCancelTarget] = useState<Job | null>(null);

  // Any filter change invalidates the current page — jump back to the first page.
  useEffect(() => {
    setPagination((p) => (p.page === 0 ? p : { ...p, page: 0 }));
  }, [projectId, jobType, status]);

  const projectsLookup = useQuery({
    queryKey: ['projects', 'select'],
    queryFn: () => resources.projects.list({ pageSize: 200, sort: 'name' }),
  });
  const projectName = useMemo(() => {
    const m = new Map<number, string>();
    for (const p of projectsLookup.data?.data ?? []) m.set(p.id, p.name);
    return m;
  }, [projectsLookup.data]);

  const filter: ListQuery['filter'] = useMemo(() => {
    const f: NonNullable<ListQuery['filter']> = {};
    if (projectId != null) f.projectId = projectId;
    if (jobType !== 'all') f.jobType = jobType;
    if (status !== 'all') f.status = status;
    return f;
  }, [projectId, jobType, status]);

  const list = useQuery({
    queryKey: ['jobs', pagination, filter],
    queryFn: () =>
      resources.jobs.list({
        page: pagination.page + 1,
        pageSize: pagination.pageSize,
        sort: '-id',
        filter,
      }),
    placeholderData: (prev) => prev,
  });

  const cancelJob = useMutation({
    mutationFn: (job: Job) => resources.jobs.cancel(job.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      setCancelTarget(null);
      setSelected(null);
    },
  });

  const columns: GridColDef[] = useMemo(
    () => [
      { field: 'id', headerName: 'ID', width: 80 },
      {
        field: 'createdAt',
        headerName: 'Created',
        width: 170,
        renderCell: (p) => new Date(p.value as string).toLocaleString(),
      },
      {
        field: 'projectId',
        headerName: 'Project',
        width: 180,
        renderCell: (p) => (p.value ? projectName.get(p.value as number) ?? `#${p.value}` : '—'),
      },
      { field: 'jobType', headerName: 'Job Type', width: 240 },
      { field: 'priority', headerName: 'Priority', width: 100 },
      {
        field: 'progressPercent',
        headerName: 'Progress',
        width: 100,
        type: 'number',
        renderCell: (p) => `${p.value}%`,
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 130,
        sortable: false,
        renderCell: (p) => (
          <Chip
            label={p.value as string}
            size="small"
            color={STATUS_COLORS[p.value as JobStatus]}
            variant="outlined"
          />
        ),
      },
      {
        field: 'duration',
        headerName: 'Duration',
        width: 120,
        sortable: false,
        renderCell: (p) => {
          const job = p.row as Job;
          return job.status === 'COMPLETED' ? formatDuration(job) : '—';
        },
      },
      {
        field: 'actions',
        headerName: '',
        width: 60,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (p) => {
          const job = p.row as Job;
          if (!CANCELLABLE_STATUSES.includes(job.status)) return null;
          return (
            <Tooltip title="Cancel job">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setCancelTarget(job);
                }}
              >
                <CancelIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          );
        },
      },
    ],
    [projectName],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <PageHeader
        title="Job Monitoring"
        description="Audit log of background jobs across projects. Click a row to view the full job record."
      />

      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        <Box sx={{ minWidth: 260 }}>
          <EntitySelect<Project>
            label="Project"
            cacheKey={['projects', 'select']}
            service={resources.projects}
            value={projectId}
            onChange={setProjectId}
          />
        </Box>
        <TextField
          select
          label="Job Type"
          value={jobType}
          onChange={(e) => setJobType(e.target.value as JobType | 'all')}
          sx={{ minWidth: 260 }}
        >
          <MenuItem value="all">All</MenuItem>
          {JOB_TYPES.map((t) => (
            <MenuItem key={t} value={t}>{t}</MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Status"
          value={status}
          onChange={(e) => setStatus(e.target.value as JobStatus | 'all')}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="all">All</MenuItem>
          {JOB_STATUSES.map((s) => (
            <MenuItem key={s} value={s}>{s}</MenuItem>
          ))}
        </TextField>
        <Button
          startIcon={<RefreshIcon />}
          onClick={() => list.refetch()}
          disabled={list.isFetching}
        >
          Reload
        </Button>
      </Stack>

      <DataTable<Job>
        rows={list.data?.data ?? []}
        columns={columns}
        loading={list.isFetching}
        rowCount={list.data?.page.total ?? 0}
        paginationMode="server"
        paginationModel={pagination}
        onPaginationModelChange={setPagination}
        errorMessage={list.isError ? extractErrorMessage(list.error) : undefined}
        onRowClick={(row) => setSelected(row)}
      />

      <JobDetailDialog
        open={selected !== null}
        job={selected}
        projectName={selected?.projectId ? projectName.get(selected.projectId) : undefined}
        onClose={() => setSelected(null)}
        onCancel={selected && CANCELLABLE_STATUSES.includes(selected.status) ? () => setCancelTarget(selected) : undefined}
      />

      <ConfirmDialog
        open={cancelTarget !== null}
        title="Cancel job"
        message={`Are you sure you want to cancel job #${cancelTarget?.id} (${cancelTarget?.jobType})? This cannot be undone.`}
        confirmLabel="Cancel job"
        cancelLabel="Keep job"
        destructive
        loading={cancelJob.isPending}
        errorMessage={cancelJob.isError ? extractErrorMessage(cancelJob.error) : undefined}
        onConfirm={() => cancelTarget && cancelJob.mutate(cancelTarget)}
        onClose={() => {
          setCancelTarget(null);
          cancelJob.reset();
        }}
      />
    </Box>
  );
}
